class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');

    /*
      MANN edit:
      Desktop filters still auto-submit on input.
      Mobile filters do NOT auto-submit on checkbox tap.
      Mobile filters apply only when the user taps the main Apply button.
    */
    if (facetForm && facetForm.id !== 'FacetFiltersFormMobile') {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

      /*
        MANN search-page fix:
        Some search facet controls do not reliably fire the same input event
        path as collection facets. Add a change listener only inside the
        search filters wrapper so collection filtering behavior stays untouched.
      */
      if (this.closest('#main-search-filters')) {
        facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
      }
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer && productGridContainer.querySelector('.collection')) {
      productGridContainer.querySelector('.collection').classList.add('loading');
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }

    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newCount = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (newCount && container) {
      container.innerHTML = newCount.innerHTML;
      container.classList.remove('loading');
    }

    if (newCount && containerDesktop) {
      containerDesktop.innerHTML = newCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event && event.target && event.target.closest ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];

          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event && event.target && event.target.closest) {
      const closestJSFilter = event.target.closest('.js-filter');
      const closestJSFilterID = closestJSFilter ? closestJSFilter.id : null;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, closestJSFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute && event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentActiveFacetsElement = document.querySelector(selector);

      if (!activeFacetsElement || !currentActiveFacetsElement) return;

      currentActiveFacetsElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const newElement = html.querySelector(selector);
      const currentElement = document.querySelector(selector);

      if (!newElement || !currentElement) return;

      currentElement.innerHTML = newElement.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm ? mobileForm.closest('menu-drawer') : null;

    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));

      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    const target = event && event.target;

    /*
      MANN search-page fix:
      Keep collection filters on the existing AJAX behavior, but submit search
      filters through the actual search URL. This preserves q/options[prefix]
      and makes search filtering reliable on both desktop and mobile.
    */
    if (target && target.closest && target.closest('#main-search-filters')) {
      window.location.href = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;
      return;
    }

    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    if (event.currentTarget && event.currentTarget.closest && event.currentTarget.closest('#main-search-filters')) {
      window.location.href = event.currentTarget.href;
      return;
    }

    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;

    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink = this.querySelector('a');

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/* =========================================================
   MANN Mobile Facets UX Fix
   - Category Apply applies filters and returns to main filter panel
   - Main Apply applies filters, closes drawer, unlocks scroll
   - Hides stuck top-right X after drawer closes
   - Lets users select multiple mobile filters before applying
   - Keeps desktop behavior untouched
========================================================= */

(function () {
  const MOBILE_QUERY = '(max-width: 749px)';
  const FILTER_ROOTS = ['#main-collection-filters', '#main-search-filters'];

  function rootSelector(childSelector) {
    return FILTER_ROOTS.map(function (root) {
      return root + childSelector;
    }).join(', ');
  }

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function unlockPageScroll() {
    const unlockClasses = ['overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet'];

    unlockClasses.forEach(function (className) {
      document.documentElement.classList.remove(className);
      document.body.classList.remove(className);
    });

    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.documentElement.style.position = '';
    document.body.style.position = '';
    document.documentElement.style.touchAction = '';
    document.body.style.touchAction = '';
  }

  function hideMobileCloseButtons() {
    document.querySelectorAll(rootSelector(' .mobile-facets__close')).forEach(function (button) {
      button.style.display = 'none';
      button.style.opacity = '0';
      button.style.pointerEvents = 'none';
      button.setAttribute('aria-hidden', 'true');
    });
  }

  function restoreMobileCloseButtons() {
    document.querySelectorAll(rootSelector(' .mobile-facets__close')).forEach(function (button) {
      button.style.display = '';
      button.style.opacity = '';
      button.style.pointerEvents = '';
      button.removeAttribute('aria-hidden');
    });
  }

  function closeMobileSubmenuFromApply(button) {
    const currentDetails = button.closest('.mobile-facets__details');

    if (!currentDetails) return;

    const backButton = currentDetails.querySelector('.mobile-facets__close-button');

    if (backButton) {
      backButton.click();
      return;
    }

    currentDetails.classList.remove('menu-opening');
    currentDetails.removeAttribute('open');

    const mainPanel = currentDetails.closest('.mobile-facets__main');
    if (mainPanel) mainPanel.classList.remove('submenu-open');

    currentDetails.querySelectorAll('.submenu-open').forEach(function (element) {
      element.classList.remove('submenu-open');
    });
  }

  function closeMobileFacetSubmenus(wrapper) {
    if (!wrapper) return;

    wrapper.querySelectorAll('.mobile-facets__details[open]').forEach(function (details) {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    wrapper.querySelectorAll('.mobile-facets__submenu').forEach(function (submenu) {
      submenu.style.transform = '';
      submenu.style.visibility = '';
    });

    wrapper.querySelectorAll('.submenu-open').forEach(function (element) {
      element.classList.remove('submenu-open');
    });
  }

  function closeMobileFacetDrawer() {
    if (!isMobile()) return;

    document.querySelectorAll(rootSelector(' .mobile-facets__wrapper')).forEach(function (wrapper) {
      const rootDetails = wrapper.querySelector('details.mobile-facets__disclosure, details.disclosure-has-popup');
      const rootSummary = wrapper.querySelector('.mobile-facets__open');
      const menuDrawer = wrapper.closest('menu-drawer') || wrapper;

      closeMobileFacetSubmenus(wrapper);

      if (menuDrawer && typeof menuDrawer.closeMenuDrawer === 'function') {
        try {
          menuDrawer.closeMenuDrawer(new Event('click'), rootSummary || false);
        } catch (error) {
          /* fallback continues below */
        }
      }

      wrapper.classList.remove('menu-opening');

      wrapper.querySelectorAll('.menu-opening').forEach(function (element) {
        element.classList.remove('menu-opening');
      });

      wrapper.querySelectorAll('details.mobile-facets__disclosure, details.disclosure-has-popup').forEach(function (details) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });

      if (rootDetails) {
        rootDetails.removeAttribute('open');
        rootDetails.classList.remove('menu-opening');
      }

      if (rootSummary) {
        rootSummary.setAttribute('aria-expanded', 'false');
      }

      wrapper.querySelectorAll('.mobile-facets').forEach(function (drawer) {
        drawer.style.pointerEvents = '';
      });

      wrapper.querySelectorAll('.mobile-facets__inner').forEach(function (inner) {
        inner.style.pointerEvents = '';
        inner.style.transform = '';
      });
    });

    unlockPageScroll();
    hideMobileCloseButtons();
  }

  function closeMobileFacetDrawerAfterApply() {
    if (!isMobile()) return;

    closeMobileFacetDrawer();

    requestAnimationFrame(function () {
      closeMobileFacetDrawer();
    });

    setTimeout(closeMobileFacetDrawer, 100);
    setTimeout(closeMobileFacetDrawer, 300);
    setTimeout(closeMobileFacetDrawer, 700);
    setTimeout(closeMobileFacetDrawer, 1200);
  }

  function getApplyButton(target) {
    const button = target.closest('button, .button');

    if (!button) return null;

    const insideMobileFacets = button.closest(rootSelector(' .mobile-facets'));

    if (!insideMobileFacets) return null;

    const text = button.textContent.trim().toLowerCase();

    if (!text.includes('apply')) return null;

    return button;
  }

  function isCategoryApplyButton(button) {
    return !!button.closest('.mobile-facets__submenu');
  }

  function isMainApplyButton(button) {
    return !!button.closest('.mobile-facets__footer') && !isCategoryApplyButton(button);
  }

  function getMobileFacetFormFromButton(button) {
    const mobileForm = button.closest('form');

    if (!mobileForm || mobileForm.id !== 'FacetFiltersFormMobile') return null;

    const facetFiltersForm = mobileForm.closest('facet-filters-form');

    if (
      !facetFiltersForm ||
      typeof facetFiltersForm.createSearchParams !== 'function' ||
      typeof facetFiltersForm.onSubmitForm !== 'function'
    ) {
      return null;
    }

    return {
      mobileForm: mobileForm,
      facetFiltersForm: facetFiltersForm
    };
  }

  function applyMobileFilters(button) {
    const data = getMobileFacetFormFromButton(button);

    if (!data) return false;

    const searchParams = data.facetFiltersForm.createSearchParams(data.mobileForm);

    data.facetFiltersForm.onSubmitForm(searchParams, {
      target: data.mobileForm
    });

    return true;
  }

  function applyCategoryFiltersAndReturnToMain(button) {
    const applied = applyMobileFilters(button);

    closeMobileSubmenuFromApply(button);

    /*
      Shopify updates the drawer counts/options after AJAX.
      These delayed submenu resets keep the customer on the main filter panel
      while the available options update based on selected filters.
    */
    requestAnimationFrame(function () {
      closeMobileSubmenuFromApply(button);
    });

    setTimeout(function () {
      closeMobileSubmenuFromApply(button);
    }, 120);

    setTimeout(function () {
      const wrapper = document.querySelector(rootSelector(' .mobile-facets__wrapper'));
      if (!wrapper) return;

      wrapper.querySelectorAll('.mobile-facets__main.submenu-open').forEach(function (mainPanel) {
        mainPanel.classList.remove('submenu-open');
      });

      wrapper.querySelectorAll('.mobile-facets__details[open]').forEach(function (details) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
    }, 350);

    return applied;
  }

  function applyMainFiltersAndClose(button) {
    const applied = applyMobileFilters(button);

    closeMobileFacetDrawerAfterApply();

    return applied;
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!isMobile()) return;

      const openButton = event.target.closest(rootSelector(' .mobile-facets__open'));
      if (openButton) {
        restoreMobileCloseButtons();
        return;
      }

      const applyButton = getApplyButton(event.target);

      if (!applyButton) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (isCategoryApplyButton(applyButton)) {
        applyCategoryFiltersAndReturnToMain(applyButton);
        return;
      }

      if (isMainApplyButton(applyButton)) {
        applyMainFiltersAndClose(applyButton);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      if (!isMobile()) return;

      const form = event.target;

      if (!form || form.id !== 'FacetFiltersFormMobile') return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const facetFiltersForm = form.closest('facet-filters-form');

      if (!facetFiltersForm || typeof facetFiltersForm.createSearchParams !== 'function') return;

      const searchParams = facetFiltersForm.createSearchParams(form);

      facetFiltersForm.onSubmitForm(searchParams, {
        target: form
      });

      closeMobileFacetDrawerAfterApply();
    },
    true
  );

  document.addEventListener('DOMContentLoaded', unlockPageScroll);
  window.addEventListener('pageshow', unlockPageScroll);
})();